import { Component, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { ColumnDef } from 'src/app/model/column-definition';
import { Transaction } from 'src/app/model/transaction';
import { GreenskyService } from 'src/app/services/greensky/greensky.service';
import { DialogService } from 'src/app/services/dialog/dialog.service';
import { TransactionSum } from 'src/app/model/transactionSum';


/**
 * GreenskyPageComponent is used to render the Greensky page. As of now, this only involves
 * backbook transactions.
 */
@Component({
  selector: 'app-Greensky-page',
  templateUrl: './greensky-page.component.html',
  styleUrl: './greensky-page.component.scss'
})
export class GreenskyPageComponent {
  /** The paginator to apply to the table */
  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator | null = null;

  /** The sort element in our table */
  @ViewChild(MatSort, {static: true}) sort: MatSort = new MatSort();

  /** The table of transactions */
  @ViewChild(MatTable) table!: MatTable<Transaction>;

  /** The data to be rendered in the table */
  public source = new MatTableDataSource<Transaction>([]);

  /** Transaction sum data to be rendered above the table */
  public sums: {type: string, totalAmount: number}[] = [];

  /** Whether or not we are loading transactions */
  public loadingTransactions = false;

  /** Column definitions for the table */
  public colDefs: ColumnDef[] = [
    {header: "Date", field: "date"},
    {header: "Type", field: "type"},
    {header: "Description", field: "description"},
    {header: "Total Amount", field: "totalAmount", format: "currency"},
  ]
  public displayedCols: string[] = this.colDefs.map(x => x.field);

  /**
   * Constructor for the supplier page to initialize services and set a base snapshot 
   */
  constructor(
    private _gskyService: GreenskyService,
    private _dialogService: DialogService,
  ) {
    // Get pre-existing data from the source
    this.source.data = this._gskyService.lastTransactions;
    this.sums = this._gskyService.lastTransactionSums;
  }

  /**
   * Initialize the sort, paginator, and supplier data source
   */
  public ngOnInit() {
    this.source.sort = this.sort;
    this.source.paginator = this.paginator;

    // Add a filter predicate to search with our custom toString method with delimieters
    this.source.filterPredicate = (data: Transaction, filter: string): boolean => {
      const str = data.toString(this.colDefs);
      return str.includes(filter);
    }
  }

  /**
   * After the table gets initialized, add a sorter and custom sorting functions
   */
  public ngAfterViewInit() {
    this.source.sortingDataAccessor = (item: Transaction, property: string): string | number => {
      return (item as any)[property];
    }
  }

  /**
   * Query transactions by getting a card number from a dialog
   */
  public queryTransactions(): void {
    let completedCalls = 0;

    this._dialogService.openGetTransactions().subscribe(
      (cardNumber: number | undefined): void => {
        if (cardNumber == undefined) return;

        this.loadingTransactions = true;

        this._gskyService.getTransactions(cardNumber).subscribe(
          (transactions: Transaction[]) => {
            this.source.data = transactions;
            if (++completedCalls === 2) this.loadingTransactions = false;
          });

        this._gskyService.getTransactionsSum(cardNumber).subscribe(
          (sums: TransactionSum[]) => {
            this.sums = sums.map(x => {return {type: x.type, totalAmount: x.totalAmount}});
            if (++completedCalls === 2) this.loadingTransactions = false;
          });
      });
  }

  /**
   * Apply a filter to the table
   */
  public applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.source.filter = filterValue.trim().toLowerCase();
  }
}